import React, { useState, useEffect, useRef } from 'react';
import { expertDashboardApi } from '../../../lib/http';
import InlineClientForm from './InlineClientForm';

interface Client {
  id: string;
  name: string;
  phone: string;
  email: string;
}

interface ClientSelectorProps {
  selectedClientId: string | null;
  selectedClientName: string;
  error?: string;
  onClientSelect: (client: Client) => void;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  selectedClientId,
  selectedClientName,
  error,
  onClientSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Client[]>([]);
  const [recentClients, setRecentClients] = useState<Client[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load recent clients when component mounts
  useEffect(() => {
    loadRecentClients();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Search clients with debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    // Debounce search
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await expertDashboardApi.expertClients.search(searchQuery, 10);
        const clients = response.data.expert_clients || [];
        setSearchResults(
          clients.map((ec: any) => ({
            id: ec.id,
            name: ec.display_name,
            phone: ec.custom_phone || ec.client?.phone || '',
            email: ec.custom_email || ec.client?.email || '',
          }))
        );
        setIsDropdownOpen(true);
      } catch (error) {
        console.error('Failed to search clients:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadRecentClients = async () => {
    try {
      const response = await expertDashboardApi.expertClients.list({
        per_page: 5,
        sort: 'recent_booking',
        order: 'desc',
      });
      const clients = response.data.expert_clients || [];
      setRecentClients(
        clients.slice(0, 5).map((ec: any) => ({
          id: ec.id,
          name: ec.display_name,
          phone: ec.custom_phone || ec.client?.phone || '',
          email: ec.custom_email || ec.client?.email || '',
        }))
      );
    } catch (error) {
      console.error('Failed to load recent clients:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClientClick = (client: Client) => {
    onClientSelect(client);
    setSearchQuery('');
    setSearchResults([]);
    setIsDropdownOpen(false);
  };

  const handleRecentClientClick = (client: Client) => {
    onClientSelect(client);
  };

  const handleAddNewClient = () => {
    setShowAddForm(true);
    setIsDropdownOpen(false);
  };

  const handleClientAdded = (client: Client) => {
    onClientSelect(client);
    setShowAddForm(false);
    loadRecentClients(); // Refresh recent clients
  };

  const handleClearSelection = () => {
    onClientSelect({
      id: '',
      name: '',
      phone: '',
      email: '',
    });
  };

  return (
    <div>
      <label htmlFor="client-search" className="block text-sm font-medium text-gray-700 mb-3">
        Client <span className="text-red-500">*</span>
      </label>

      {selectedClientId ? (
        // Selected client card
        <div className="p-4 bg-purple-50 border-2 border-purple-600 rounded-xl">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900">{selectedClientName}</div>
              <button
                type="button"
                onClick={handleClearSelection}
                className="text-sm text-purple-600 hover:text-purple-700 underline mt-1"
              >
                Change
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Search Input */}
          <div ref={dropdownRef} className="relative">
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                id="client-search"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setIsDropdownOpen(true)}
                className={`block w-full pl-12 pr-4 py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                  error ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Search by name or phone..."
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}
            </div>

            {/* Dropdown with search results */}
            {isDropdownOpen && (searchResults.length > 0 || searchQuery.trim().length >= 2) && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-y-auto">
                {searchResults.length > 0 ? (
                  searchResults.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleClientClick(client)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.phone || client.email}</div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    No clients found for "{searchQuery}"
                  </div>
                )}
                {searchQuery.trim().length >= 2 && (
                  <button
                    type="button"
                    onClick={handleAddNewClient}
                    className="w-full text-left px-4 py-3 text-purple-600 hover:bg-purple-50 border-t-2 border-gray-200 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add "{searchQuery.trim()}" as new client
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Recent Clients */}
          {!showAddForm && recentClients.length > 0 && (
            <div className="mt-3">
              <div className="text-sm text-gray-600 mb-2">Recent Clients:</div>
              <div className="flex flex-wrap gap-2">
                {recentClients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleRecentClientClick(client)}
                    className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium border border-purple-200 hover:bg-purple-200 active:scale-95 transition-all"
                  >
                    {client.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add New Client Link */}
          {!showAddForm && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleAddNewClient}
                className="text-sm text-purple-600 hover:text-purple-700 underline"
              >
                + Add new client
              </button>
            </div>
          )}

          {/* Inline Add Client Form */}
          {showAddForm && (
            <InlineClientForm
              onCancel={() => setShowAddForm(false)}
              onSuccess={handleClientAdded}
              prefilledName={searchQuery}
            />
          )}
        </>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default ClientSelector;
